/**
 * Organisation filter for the Bulk Invoices page (Trello #363, Marcus 2026-08-03).
 *
 * Replaces the original "Only organisations with billing details" checkbox. That
 * checkbox filtered on whether an org had a billing NAME + ADDRESS — a
 * prerequisite for rendering an invoice, but not an answer to the question an
 * admin actually asks here, which is "who am I invoicing?". Marcus asked for
 * "Paying organisations" plus a breakdown by how they pay: Subscribing / EFT /
 * Managed.
 *
 * Deliberately free of React / MUI / api-client imports (same reasoning as
 * `bulk-invoice-periods.ts`) so the page, its unit tests, and the Playwright
 * walkthrough spec all share ONE implementation of the predicate rather than
 * re-rolling it per consumer.
 *
 * The channel predicates themselves live in `utils/org-payment-signal` — the
 * canonical home for "how does this org pay" — so this module composes them and
 * does not re-define what EFT/managed/subscribing mean.
 */

import type { Organization } from '../services/api';
import {
  isEftOrg,
  isManagedOrg,
  isSubscribingOrg,
  ORG_AUDIENCE_ALL_LABEL,
  ORG_AUDIENCE_PAYING_LABEL,
  type OrgBillingSignals,
} from '../utils/org-payment-signal';

export type BulkInvoiceOrgFilter =
  | 'all'
  | 'paying'
  | 'subscribing'
  | 'eft'
  | 'managed';

/** Menu order — "all" first, then paying and its three channels. */
export const BULK_INVOICE_ORG_FILTERS: readonly BulkInvoiceOrgFilter[] = [
  'all',
  'paying',
  'subscribing',
  'eft',
  'managed',
];

/**
 * The label each option renders as. Exported so the page's dropdown, the
 * "All <label>" checkbox, and the tests all read the same strings instead of
 * three copies that can drift.
 *
 * The two AUDIENCE labels come from `org-payment-signal` rather than being
 * spelled out here: the announcement-email dialog (Trello #395) offers the same
 * two options, and a reader who sees "Paying organisations" in both places is
 * entitled to assume they mean the same set. The three channel labels below stay
 * local — they answer HOW an org pays, which is this page's question alone.
 */
export const BULK_INVOICE_ORG_FILTER_LABELS: Record<BulkInvoiceOrgFilter, string> = {
  all: ORG_AUDIENCE_ALL_LABEL,
  paying: ORG_AUDIENCE_PAYING_LABEL,
  subscribing: 'Subscribing organisations',
  eft: 'EFT organisations',
  managed: 'Managed organisations',
};

/**
 * An organisation we invoice: it pays through ANY of the three channels — a live
 * self-serve subscription, a direct EFT, or a managed (invoiced-by-us) plan.
 *
 * Deliberately broader than `org-payment-signal.isPayingOrg`, which is
 * REVENUE-focused and excludes managed because a comp'd plan contributes no
 * self-serve MRR. Managed orgs still receive an invoice, so on this page they
 * belong in "paying" — the same definition `BillingTagChip.PAYING_TAGS` uses for
 * the Usage Health "Paying only" filter.
 */
export function isInvoicedOrg(org: OrgBillingSignals): boolean {
  return isSubscribingOrg(org) || isEftOrg(org) || isManagedOrg(org);
}

/** Whether an organisation belongs in the given filter's result set. */
export function matchesBulkInvoiceFilter(
  org: OrgBillingSignals,
  filter: BulkInvoiceOrgFilter,
): boolean {
  switch (filter) {
    case 'paying':
      return isInvoicedOrg(org);
    case 'subscribing':
      return isSubscribingOrg(org);
    case 'eft':
      return isEftOrg(org);
    case 'managed':
      return isManagedOrg(org);
    case 'all':
    default:
      return true;
  }
}

/**
 * Whether the loaded organisations carry ANY billing signal at all.
 *
 * `subscriptionTag` / `eftPayer` / `billingMode` are broker-AI concerns returned
 * by the backend's org-subscriptions join. Against the sales-led IA build — or a
 * backend deployed BEHIND this frontend, which is the normal ordering when the
 * admin ships first — every org has all three undefined. A "Paying
 * organisations" default would then match nothing and the page would render an
 * empty list that reads as "we have no customers" rather than "this build has no
 * billing data".
 *
 * So the page hides the filter entirely (and shows every org) when this is
 * false. An all-present-but-all-non-paying set still returns true: "0 paying" is
 * a truthful answer and must NOT be hidden.
 */
export function hasBillingSignalData(orgs: readonly OrgBillingSignals[]): boolean {
  return orgs.some(
    (o) =>
      o.subscriptionTag !== undefined ||
      o.eftPayer !== undefined ||
      o.billingMode !== undefined,
  );
}

/** Convenience for the page: the orgs matching `filter`, in input order. */
export function filterOrganizations(
  orgs: readonly Organization[],
  filter: BulkInvoiceOrgFilter,
): Organization[] {
  return orgs.filter((o) => matchesBulkInvoiceFilter(o, filter));
}
